import React, { useEffect, useMemo, useState } from "react";
import fetchData from "verticales/FetchData";
import PropTypes from "prop-types";
import ReactECharts from "echarts-for-react";
import {
  Card,
  Grid,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Chip,
  Divider,
  CardHeader,
  Alert,
  Paper,
  Tabs,
  Tab,
  Avatar,
} from "@mui/material";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import CardContent from "@mui/material/CardContent";
import AppLoading from "verticales/AppLoading.tsx";
import CloseIcon from "@mui/icons-material/Close";
import StorageIcon from "@mui/icons-material/Storage";
import CloudIcon from "@mui/icons-material/Cloud";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PieChartIcon from "@mui/icons-material/PieChart";
import BarChartIcon from "@mui/icons-material/BarChart";
import TimelineIcon from "@mui/icons-material/Timeline";
import TableChartIcon from "@mui/icons-material/TableChart";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

const url = "https://5djo5qwa3l.execute-api.us-east-1.amazonaws.com/dimo-goyito/ec2/";

const currency = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  maximumFractionDigits: 0,
});

const currencyDetailed = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  maximumFractionDigits: 2,
});

function getRowsFromResult(result) {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.costosCliente)) return result.costosCliente;
  if (Array.isArray(result?.costosCliente?.rows)) return result.costosCliente.rows;
  return [];
}

function getLicenciasFromResult(result) {
  const val = result?.licenciasCliente?.rows?.[0]?.numbers_users;
  const n = parseInt(val, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function formatAnioMes(anioMes) {
  if (!anioMes || anioMes.length !== 6) return anioMes;
  const meses = [
    "Ene",
    "Feb",
    "Mar",
    "Abr",
    "May",
    "Jun",
    "Jul",
    "Ago",
    "Sep",
    "Oct",
    "Nov",
    "Dic",
  ];
  const anio = anioMes.slice(0, 4);
  const mes = parseInt(anioMes.slice(4, 6), 10);
  return `${meses[mes - 1]} ${anio}`;
}

// Paleta de colores para períodos — accesible en todo el componente
const MONTH_COLORS = ["#137af0", "#0f9d58", "#f97316", "#7b1fa2", "#e53935", "#00acc1"];

// ─── KPI Card ────────────────────────────────────────────────────────────────
const CardStat = ({ title, value, gradient, icon: Icon, subtitle, highlight }) => (
  <Card
    elevation={0}
    sx={{
      height: "100%",
      borderRadius: 3,
      background: gradient,
      color: "white",
      position: "relative",
      overflow: "hidden",
      transition: "transform 0.3s ease, box-shadow 0.3s ease",
      "&:hover": {
        transform: "translateY(-6px)",
        boxShadow: "0 20px 40px rgba(0,0,0,0.25)",
      },
      "&::after": highlight
        ? {
            content: '""',
            position: "absolute",
            top: -30,
            right: -30,
            width: 100,
            height: 100,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.12)",
          }
        : {},
    }}
  >
    <CardContent sx={{ p: 3, position: "relative", zIndex: 1 }}>
      <Box
        sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}
      >
        <Box
          sx={{
            backgroundColor: "rgba(255,255,255,0.18)",
            borderRadius: 2,
            p: 1.2,
            backdropFilter: "blur(10px)",
            display: "flex",
          }}
        >
          <Icon sx={{ fontSize: 28 }} />
        </Box>
        {highlight && (
          <Chip
            label="Último mes"
            size="small"
            sx={{
              backgroundColor: "rgba(255,255,255,0.25)",
              color: "white",
              fontWeight: 700,
              fontSize: "0.65rem",
              height: 22,
            }}
          />
        )}
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: "white",
          opacity: 0.88,
          textTransform: "uppercase",
          letterSpacing: 1.2,
          fontWeight: 700,
          display: "block",
          mb: 1,
        }}
      >
        {title}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2, mb: 0.5 }}>
        {typeof value === "number" ? currency.format(value) : value}
      </Typography>
      {subtitle && (
        <Typography
          variant="caption"
          sx={{ opacity: 0.82, display: "block", mt: 0.5, lineHeight: 1.4 }}
        >
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

CardStat.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  gradient: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  subtitle: PropTypes.string,
  highlight: PropTypes.bool,
};

// ─── Licencias Banner ────────────────────────────────────────────────────────
const LicenciasBanner = ({ licencias, costoUltMes, ultimoMesLabel }) => {
  const costoPorUsuario = licencias > 0 ? costoUltMes / licencias : null;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        background: "linear-gradient(135deg, #edf5f1 60%, #137af0 40%)",
        color: "white",
        p: 3,
        mb: 3,
        display: "flex",
        alignItems: "center",
        gap: 3,
        flexWrap: "wrap",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: -40,
          right: -40,
          width: 200,
          height: 200,
          borderRadius: "50%",
          background: "rgba(19, 122, 240, 0.15)",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: -60,
          right: 80,
          width: 150,
          height: 150,
          borderRadius: "50%",
          background: "rgba(56, 239, 125, 0.1)",
        },
      }}
    >
      {/* Icono principal */}
      <Box
        sx={{
          backgroundColor: "rgba(19, 122, 240, 0.25)",
          borderRadius: 3,
          p: 2,
          display: "flex",
          border: "1px solid rgba(19, 122, 240, 0.4)",
        }}
      >
        <PeopleAltIcon sx={{ fontSize: 40, color: "#5ba3f5" }} />
      </Box>

      {/* Licencias */}
      <Box sx={{ flex: 1, minWidth: 140 }}>
        <Typography
          variant="caption"
          sx={{ opacity: 0.7, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}
        >
          Licencias Activas
        </Typography>
        <Typography variant="h3" sx={{ fontWeight: 900, lineHeight: 1, color: "#050505" }}>
          {licencias !== null ? licencias.toLocaleString("es-CO") : "—"}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          usuarios registrados
        </Typography>
      </Box>

      <Divider
        orientation="vertical"
        flexItem
        sx={{ borderColor: "rgba(255,255,255,0.15)", mx: 1 }}
      />

      {/* Costo por usuario */}
      <Box sx={{ flex: 2, minWidth: 200 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <PersonIcon sx={{ fontSize: 18, opacity: 0.7 }} />
          <Typography
            variant="caption"
            sx={{ opacity: 0.7, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}
          >
            Costo por Usuario
          </Typography>
        </Box>
        {costoPorUsuario !== null ? (
          <>
            <Typography variant="h4" sx={{ fontWeight: 800, color: "#080808", lineHeight: 1 }}>
              {currencyDetailed.format(costoPorUsuario)}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              basado en costos de {ultimoMesLabel}
            </Typography>
          </>
        ) : (
          <Typography variant="body1" sx={{ opacity: 0.5, fontStyle: "italic" }}>
            Sin datos de licencias disponibles
          </Typography>
        )}
      </Box>

      <Divider
        orientation="vertical"
        flexItem
        sx={{ borderColor: "rgba(255,255,255,0.15)", mx: 1 }}
      />

      {/* Periodo */}
      <Box sx={{ minWidth: 130 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <CalendarTodayIcon sx={{ fontSize: 18, opacity: 0.8 }} />
          <Typography
            variant="caption"
            sx={{ opacity: 0.8, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}
          >
            Periodo
          </Typography>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "white" }}>
          {ultimoMesLabel}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          último mes disponible
        </Typography>
      </Box>
    </Paper>
  );
};

LicenciasBanner.propTypes = {
  licencias: PropTypes.number,
  costoUltMes: PropTypes.number.isRequired,
  ultimoMesLabel: PropTypes.string.isRequired,
};

// ─── Componente Principal ─────────────────────────────────────────────────────
const CostosCliente = (props) => {
  const dbName = props?.row?.original?.database_name || "";
  const [rows, setRows] = useState([]);
  const [licencias, setLicencias] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  // ── Columnas tabla ──────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        header: "RDS ID",
        accessorKey: "rds_id",
        cell: (info) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <StorageIcon sx={{ fontSize: 18, color: "primary.main" }} />
            <Typography variant="body2" fontWeight={600}>
              {info.getValue()}
            </Typography>
          </Box>
        ),
      },
      {
        header: "Período",
        accessorKey: "anio_mes",
        cell: (info) => (
          <Chip
            icon={<CalendarTodayIcon sx={{ fontSize: 14 }} />}
            label={formatAnioMes(info.getValue())}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        ),
      },
      {
        header: "Base de Datos",
        accessorKey: "database_name",
        cell: (info) => (
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {info.getValue()}
          </Typography>
        ),
      },
      {
        header: "Tipo",
        accessorKey: "tipo",
        cell: (info) => (
          <Chip
            label={info.getValue()}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        ),
      },
      {
        header: "Artefacto",
        accessorKey: "artefacto",
        cell: (info) => {
          const value = info.getValue();
          const isEC2 = value?.toUpperCase() === "EC2";
          return (
            <Chip
              icon={
                isEC2 ? <CloudIcon sx={{ fontSize: 16 }} /> : <StorageIcon sx={{ fontSize: 16 }} />
              }
              label={value}
              size="small"
              color={isEC2 ? "info" : "success"}
              sx={{ fontWeight: 600 }}
            />
          );
        },
      },
      {
        header: "Costo Total",
        accessorKey: "costo_total_cliente",
        cell: (info) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <AttachMoneyIcon sx={{ fontSize: 16, color: "success.main" }} />
            <Typography variant="body2" fontWeight={700} color="success.main">
              {currencyDetailed.format(parseFloat(info.getValue() || 0))}
            </Typography>
          </Box>
        ),
      },
    ],
    []
  );

  const [rowSelect, setRowSelect] = useState({});
  const [openModal, setOpenModal] = useState(false);

  const handleOpenModal = (row) => {
    setRowSelect(row);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    if (props.handleCloseModal) props.handleCloseModal();
    setOpenModal(false);
  };

  const renderOpcRowsSid = (row) => (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%", p: 0 }}>
      <Tooltip title="Ver Análisis Detallado" arrow placement="left">
        <IconButton
          onClick={() => handleOpenModal(row)}
          sx={{
            color: "#137af0",
            "&:hover": { backgroundColor: "primary.light", color: "white" },
            transition: "all 0.3s ease",
          }}
        >
          <AnalyticsIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );

  // ── Fetch ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchDataFromApi = async () => {
      try {
        setLoading(true);
        setError("");
        const params = { action: "get-cost-client", database: dbName };
        const result = await fetchData(url, "costos", params);
        const dataRows = getRowsFromResult(result);
        const dataLicencias = getLicenciasFromResult(result);
        setRows(dataRows);
        setLicencias(dataLicencias);
      } catch (e) {
        console.error("Error al consultar el Api", e);
        setError("No fue posible cargar los datos de costos del cliente.");
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    if (dbName) {
      fetchDataFromApi();
    } else {
      setRows([]);
      setLoading(false);
    }
  }, [dbName]);

  // ── Métricas generales (todos los meses) ───────────────────────────────────
  const { totalEC2All, totalRDSAll, totalAll, ec2Count, rdsCount } = useMemo(() => {
    const toNum = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    };
    let ec2 = 0,
      rds = 0,
      ec2c = 0,
      rdsc = 0;
    for (const r of rows) {
      const costo = toNum(r?.costo_total_cliente);
      const art = (r?.artefacto || "").toUpperCase();
      if (art === "EC2") {
        ec2 += costo;
        ec2c++;
      } else if (art === "DB" || art === "RDS") {
        rds += costo;
        rdsc++;
      }
    }
    return {
      totalEC2All: ec2,
      totalRDSAll: rds,
      totalAll: ec2 + rds,
      ec2Count: ec2c,
      rdsCount: rdsc,
    };
  }, [rows]);

  // ── Métricas del último mes ────────────────────────────────────────────────
  const { ultimoMes, ultimoMesLabel, rowsUltimoMes, totalEC2UltMes, totalRDSUltMes, totalUltMes } =
    useMemo(() => {
      if (!rows.length)
        return {
          ultimoMes: "",
          ultimoMesLabel: "—",
          rowsUltimoMes: [],
          totalEC2UltMes: 0,
          totalRDSUltMes: 0,
          totalUltMes: 0,
        };

      const toNum = (v) => {
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : 0;
      };
      const meses = [...new Set(rows.map((r) => r.anio_mes))].sort();
      const ultimo = meses[meses.length - 1];
      const filtradas = rows.filter((r) => r.anio_mes === ultimo);

      let ec2 = 0,
        rds = 0;
      for (const r of filtradas) {
        const costo = toNum(r?.costo_total_cliente);
        const art = (r?.artefacto || "").toUpperCase();
        if (art === "EC2") ec2 += costo;
        else if (art === "DB" || art === "RDS") rds += costo;
      }
      return {
        ultimoMes: ultimo,
        ultimoMesLabel: formatAnioMes(ultimo),
        rowsUltimoMes: filtradas,
        totalEC2UltMes: ec2,
        totalRDSUltMes: rds,
        totalUltMes: ec2 + rds,
      };
    }, [rows]);

  // ── Tendencia mensual real ─────────────────────────────────────────────────
  const mesesOrdenados = useMemo(() => [...new Set(rows.map((r) => r.anio_mes))].sort(), [rows]);

  const trendData = useMemo(() => {
    const toNum = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    };
    return mesesOrdenados.map((mes) => {
      const filtradas = rows.filter((r) => r.anio_mes === mes);
      let ec2 = 0,
        rds = 0;
      for (const r of filtradas) {
        const costo = toNum(r?.costo_total_cliente);
        const art = (r?.artefacto || "").toUpperCase();
        if (art === "EC2") ec2 += costo;
        else if (art === "DB" || art === "RDS") rds += costo;
      }
      return { mes, label: formatAnioMes(mes), ec2, rds, total: ec2 + rds };
    });
  }, [rows, mesesOrdenados]);

  // ── Gráfico pastel ─────────────────────────────────────────────────────────
  const pieChartOption = useMemo(
    () => ({
      tooltip: {
        trigger: "item",
        formatter: (params) =>
          `${params.name}<br/>${currencyDetailed.format(params.value)}<br/>${params.percent}%`,
      },
      legend: { orient: "vertical", left: "left", textStyle: { fontSize: 12, fontWeight: 600 } },
      series: [
        {
          name: "Distribución de Costos",
          type: "pie",
          radius: ["40%", "70%"],
          avoidLabelOverlap: false,
          itemStyle: { borderRadius: 10, borderColor: "#fff", borderWidth: 2 },
          label: { show: true, formatter: "{b}\n{d}%", fontWeight: 600 },
          emphasis: {
            label: { show: true, fontSize: 16, fontWeight: "bold" },
            itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: "rgba(0,0,0,0.5)" },
          },
          data: [
            { value: totalEC2UltMes, name: "EC2", itemStyle: { color: "#137af0" } },
            { value: totalRDSUltMes, name: "RDS / DB", itemStyle: { color: "#38ef7d" } },
          ],
        },
      ],
    }),
    [totalEC2UltMes, totalRDSUltMes]
  );

  // ── Datos pivotados: servicio → { mes: costo } ────────────────────────────
  const pivotData = useMemo(() => {
    const toNum = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    };
    // Mapa: tipo → { anio_mes: costo, artefacto }
    const map = {};
    for (const r of rows) {
      const tipo = r.tipo || "N/A";
      if (!map[tipo]) map[tipo] = { artefacto: r.artefacto };
      map[tipo][r.anio_mes] = (map[tipo][r.anio_mes] || 0) + toNum(r.costo_total_cliente);
    }
    // Calcular total por servicio (suma de todos los meses) para ordenar Top 10
    const entries = Object.entries(map).map(([tipo, data]) => {
      const total = mesesOrdenados.reduce((acc, m) => acc + (data[m] || 0), 0);
      return { tipo, artefacto: data.artefacto, total, ...data };
    });
    entries.sort((a, b) => b.total - a.total);
    return entries;
  }, [rows, mesesOrdenados]);

  // MONTH_COLORS se define a nivel módulo (ver abajo del import)

  // ── Gráfico barras agrupadas: Top 10 servicios × todos los meses ──────────
  const barChartOption = useMemo(() => {
    const top10 = pivotData.slice(0, 10);
    const servicios = top10.map((d) => d.tipo);

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          let html = `<b>${params[0].axisValue}</b><br/>`;
          params.forEach((p) => {
            html += `${p.marker} ${p.seriesName}: <b>${currencyDetailed.format(p.value)}</b><br/>`;
          });
          const total = params.reduce((s, p) => s + (p.value || 0), 0);
          html += `<hr style="margin:4px 0"/><b>Total: ${currencyDetailed.format(total)}</b>`;
          return html;
        },
      },
      legend: {
        data: mesesOrdenados.map(formatAnioMes),
        top: 4,
        textStyle: { fontWeight: 600, fontSize: 12 },
      },
      grid: { left: "2%", right: "3%", bottom: "3%", top: "60px", containLabel: true },
      xAxis: {
        type: "category",
        data: servicios,
        axisLabel: {
          fontSize: 10,
          fontWeight: 600,
          interval: 0,
          rotate: servicios.length > 5 ? 20 : 0,
        },
      },
      yAxis: {
        type: "value",
        axisLabel: { formatter: (v) => currency.format(v), fontSize: 10 },
        splitLine: { lineStyle: { type: "dashed", color: "#e0e0e0" } },
      },
      series: mesesOrdenados.map((mes, i) => ({
        name: formatAnioMes(mes),
        type: "bar",
        barGap: "5%",
        barCategoryGap: "30%",
        data: top10.map((d) => d[mes] || 0),
        itemStyle: { color: MONTH_COLORS[i % MONTH_COLORS.length], borderRadius: [3, 3, 0, 0] },
        label: {
          show: false,
        },
        emphasis: {
          label: {
            show: true,
            position: "top",
            formatter: (p) => currency.format(p.value),
            fontSize: 10,
            fontWeight: 700,
          },
        },
      })),
    };
  }, [pivotData, mesesOrdenados]);

  // ── Gauge presupuesto ──────────────────────────────────────────────────────
  const gaugeChartOption = useMemo(() => {
    const percentage = totalUltMes > 0 ? Math.min((totalUltMes / 10000000) * 100, 100) : 0;
    return {
      series: [
        {
          type: "gauge",
          startAngle: 180,
          endAngle: 0,
          min: 0,
          max: 100,
          splitNumber: 10,
          itemStyle: {
            color: percentage > 80 ? "#ee0979" : percentage > 50 ? "#ff6a00" : "#38ef7d",
          },
          progress: { show: true, width: 18 },
          pointer: { show: false },
          axisLine: { lineStyle: { width: 18 } },
          axisTick: { show: false },
          splitLine: { length: 15, lineStyle: { width: 2, color: "#999" } },
          axisLabel: { distance: 25, color: "#999", fontSize: 12 },
          anchor: { show: false },
          title: { show: false },
          detail: {
            valueAnimation: true,
            width: "60%",
            lineHeight: 40,
            borderRadius: 8,
            offsetCenter: [0, "-15%"],
            fontSize: 32,
            fontWeight: "bolder",
            formatter: "{value}%",
            color: "inherit",
          },
          data: [{ value: percentage.toFixed(1) }],
        },
      ],
    };
  }, [totalUltMes]);

  // ── Gráfico línea con datos reales ────────────────────────────────────────
  const lineChartOption = useMemo(
    () => ({
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        formatter: (params) => {
          let result = `${params[0].axisValue}<br/>`;
          params.forEach((p) => {
            result += `${p.marker} ${p.seriesName}: ${currencyDetailed.format(p.value)}<br/>`;
          });
          return result;
        },
      },
      legend: { data: ["EC2", "RDS / DB", "Total"], top: 0 },
      grid: { left: "3%", right: "4%", bottom: "3%", top: "14%", containLabel: true },
      xAxis: { type: "category", boundaryGap: false, data: trendData.map((d) => d.label) },
      yAxis: { type: "value", axisLabel: { formatter: (v) => currency.format(v) } },
      series: [
        {
          name: "EC2",
          type: "line",
          smooth: true,
          data: trendData.map((d) => d.ec2),
          itemStyle: { color: "#137af0" },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(19,122,240,0.3)" },
                { offset: 1, color: "rgba(19,122,240,0.02)" },
              ],
            },
          },
        },
        {
          name: "RDS / DB",
          type: "line",
          smooth: true,
          data: trendData.map((d) => d.rds),
          itemStyle: { color: "#38ef7d" },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(56,239,125,0.3)" },
                { offset: 1, color: "rgba(56,239,125,0.02)" },
              ],
            },
          },
        },
        {
          name: "Total",
          type: "line",
          smooth: true,
          data: trendData.map((d) => d.total),
          itemStyle: { color: "#f97316" },
          lineStyle: { width: 3, type: "dashed" },
          symbol: "circle",
          symbolSize: 8,
        },
      ],
    }),
    [trendData]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <MDBox py={2}>
        <Card
          elevation={4}
          sx={{ borderRadius: 4, overflow: "hidden", bgcolor: "background.paper" }}
        >
          {/* ── Header ─────────────────────────────────────────────────────── */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #edf5f1 55%, #137af0 45%)",
              color: "white",
              px: 3,
              py: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  backgroundColor: "rgba(19,122,240,0.15)",
                  borderRadius: 2,
                  p: 1.5,
                  display: "flex",
                  backdropFilter: "blur(10px)",
                }}
              >
                <TrendingUpIcon sx={{ fontSize: 32, color: "#137af0" }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5, color: "#1a1a2e" }}>
                  Dashboard Ejecutivo de Costos
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <StorageIcon sx={{ fontSize: 18, color: "#137af0" }} />
                  <Typography variant="body2" sx={{ color: "#444", fontWeight: 600 }}>
                    {dbName || "No especificado"}
                  </Typography>
                  {ultimoMesLabel !== "—" && (
                    <Chip
                      icon={<CalendarTodayIcon sx={{ fontSize: 14 }} />}
                      label={`Datos hasta ${ultimoMesLabel}`}
                      size="small"
                      sx={{ ml: 1, fontWeight: 600, fontSize: "0.72rem" }}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            </Box>
            <Tooltip title="Cerrar" arrow>
              <IconButton
                aria-label="cerrar"
                onClick={handleCloseModal}
                size="small"
                sx={{
                  color: "#0a0a0a",
                  backgroundColor: "rgba(19,122,240,0.1)",
                  "&:hover": {
                    backgroundColor: "#137af0",
                    color: "white",
                    transform: "rotate(90deg)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <MDBox p={3}>
            {loading ? (
              <Box
                sx={{
                  minHeight: 400,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AppLoading titleCustom={"cargando-dashboard-costos"} />
              </Box>
            ) : error ? (
              <Alert
                severity="error"
                sx={{ borderRadius: 2, "& .MuiAlert-icon": { fontSize: 28 } }}
              >
                <Typography variant="body1" fontWeight={600}>
                  {error}
                </Typography>
              </Alert>
            ) : (
              <>
                {/* ── Banner de licencias y costo por usuario ──────────────── */}
                <LicenciasBanner
                  licencias={licencias}
                  costoUltMes={totalUltMes}
                  ultimoMesLabel={ultimoMesLabel}
                />

                {/* ── KPIs del último mes ───────────────────────────────────── */}
                <Box sx={{ mb: 1 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    fontWeight={700}
                    letterSpacing={1.5}
                  >
                    COSTOS — {ultimoMesLabel}
                  </Typography>
                </Box>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <CardStat
                      title="Total Último Mes"
                      value={totalUltMes}
                      subtitle={`${rowsUltimoMes.length} recursos facturados`}
                      gradient="linear-gradient(135deg, #edf5f1 90%, #137af0 10%)"
                      icon={TrendingUpIcon}
                      highlight
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <CardStat
                      title="EC2 Último Mes"
                      value={totalEC2UltMes}
                      subtitle={`${
                        rowsUltimoMes.filter((r) => r.artefacto?.toUpperCase() === "EC2").length
                      } instancias`}
                      gradient="linear-gradient(135deg, #edf5f1 90%, #137af0 10%)"
                      icon={CloudIcon}
                      highlight
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <CardStat
                      title="RDS Último Mes"
                      value={totalRDSUltMes}
                      subtitle={`${
                        rowsUltimoMes.filter((r) =>
                          ["DB", "RDS"].includes(r.artefacto?.toUpperCase())
                        ).length
                      } bases de datos`}
                      gradient="linear-gradient(135deg, #edf5f1 90%, #137af0 10%)"
                      icon={StorageIcon}
                      highlight
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <CardStat
                      title="Costo / Usuario"
                      value={
                        licencias && licencias > 0
                          ? currencyDetailed.format(totalUltMes / licencias)
                          : "Sin licencias"
                      }
                      subtitle={
                        licencias && licencias > 0
                          ? `${licencias} usuarios activos`
                          : "Configure licencias en el sistema"
                      }
                      gradient="linear-gradient(135deg, #edf5f1 90%, #137af0 10%)"
                      icon={PersonIcon}
                      highlight
                    />
                  </Grid>
                </Grid>

                {/* ── Separador acumulado ───────────────────────────────────── */}
                {/* <Box sx={{ mb: 1, mt: 1 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    fontWeight={700}
                    letterSpacing={1.5}
                  >
                    Acumulado todos los períodos
                  </Typography>
                </Box>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={4}>
                    <Card
                      elevation={1}
                      sx={{ borderRadius: 2, p: 2, display: "flex", alignItems: "center", gap: 2 }}
                    >
                      <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
                        <TrendingUpIcon />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          textTransform="uppercase"
                          fontWeight={700}
                          letterSpacing={1}
                        >
                          Total Acumulado
                        </Typography>
                        <Typography variant="h6" fontWeight={800}>
                          {currency.format(totalAll)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {mesesOrdenados.length} meses
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card
                      elevation={1}
                      sx={{ borderRadius: 2, p: 2, display: "flex", alignItems: "center", gap: 2 }}
                    >
                      <Avatar sx={{ bgcolor: "#137af0", width: 48, height: 48 }}>
                        <CloudIcon />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          textTransform="uppercase"
                          fontWeight={700}
                          letterSpacing={1}
                        >
                          EC2 Acumulado
                        </Typography>
                        <Typography variant="h6" fontWeight={800}>
                          {currency.format(totalEC2All)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ec2Count} registros
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card
                      elevation={1}
                      sx={{ borderRadius: 2, p: 2, display: "flex", alignItems: "center", gap: 2 }}
                    >
                      <Avatar sx={{ bgcolor: "#0f9d58", width: 48, height: 48 }}>
                        <StorageIcon />
                      </Avatar>
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          textTransform="uppercase"
                          fontWeight={700}
                          letterSpacing={1}
                        >
                          RDS Acumulado
                        </Typography>
                        <Typography variant="h6" fontWeight={800}>
                          {currency.format(totalRDSAll)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {rdsCount} registros
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                </Grid> */}

                {/* ── Tabs ─────────────────────────────────────────────────── */}
                <Paper sx={{ mb: 3, borderRadius: 2 }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{
                      borderBottom: 1,
                      borderColor: "divider",
                      "& .MuiTab-root": { fontWeight: 600, fontSize: "0.9rem" },
                    }}
                  >
                    <Tab icon={<PieChartIcon />} iconPosition="start" label="Distribución" />
                    <Tab icon={<BarChartIcon />} iconPosition="start" label="Top 10 Recursos" />
                    <Tab icon={<TimelineIcon />} iconPosition="start" label="Tendencia" />
                    <Tab icon={<TableChartIcon />} iconPosition="start" label="Detalle" />
                  </Tabs>
                </Paper>

                {/* ── Tab 0: Distribución ───────────────────────────────────── */}
                {tabValue === 0 && (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                      <Card elevation={2} sx={{ borderRadius: 3, height: "100%" }}>
                        <CardHeader
                          avatar={<PieChartIcon color="primary" />}
                          title={
                            <Typography variant="h6" fontWeight={700}>
                              Distribución de Costos — {ultimoMesLabel}
                            </Typography>
                          }
                          subheader="Comparativa entre EC2 y RDS del último mes"
                        />
                        <Divider />
                        <CardContent>
                          <ReactECharts option={pieChartOption} style={{ height: "350px" }} />
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Card elevation={2} sx={{ borderRadius: 3, height: "100%" }}>
                        <CardHeader
                          avatar={<TrendingUpIcon color="success" />}
                          title={
                            <Typography variant="h6" fontWeight={700}>
                              % de Participación del Cliente
                            </Typography>
                          }
                          subheader={`Basado en costos de ${ultimoMesLabel}`}
                        />
                        <Divider />
                        <CardContent>
                          <ReactECharts option={gaugeChartOption} style={{ height: "300px" }} />
                          <Box sx={{ textAlign: "center", mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Presupuesto de referencia: {currency.format(597479)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Consumido ({ultimoMesLabel}): {currencyDetailed.format(totalUltMes)}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}

                {/* ── Tab 1: Top 10 comparativo multi-mes ──────────────────── */}
                {tabValue === 1 && (
                  <Card elevation={2} sx={{ borderRadius: 3 }}>
                    <CardHeader
                      avatar={<BarChartIcon color="primary" />}
                      title={
                        <Typography variant="h6" fontWeight={700}>
                          Top 10 Servicios — Comparativo por Período
                        </Typography>
                      }
                      subheader={`Comparativa de los ${mesesOrdenados.length} períodos disponibles · ordenado por costo total acumulado`}
                      action={
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                            pr: 1,
                            pt: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          {mesesOrdenados.map((mes, i) => (
                            <Chip
                              key={mes}
                              label={formatAnioMes(mes)}
                              size="small"
                              sx={{
                                backgroundColor: MONTH_COLORS[i % MONTH_COLORS.length],
                                color: "white",
                                fontWeight: 700,
                                fontSize: "0.72rem",
                              }}
                            />
                          ))}
                        </Box>
                      }
                    />
                    <Divider />
                    <CardContent>
                      <ReactECharts option={barChartOption} style={{ height: "480px" }} />
                    </CardContent>
                  </Card>
                )}

                {/* ── Tab 2: Tendencia ──────────────────────────────────────── */}
                {tabValue === 2 && (
                  <Card elevation={2} sx={{ borderRadius: 3 }}>
                    <CardHeader
                      avatar={<TimelineIcon color="primary" />}
                      title={
                        <Typography variant="h6" fontWeight={700}>
                          Tendencia de Costos por Período
                        </Typography>
                      }
                      subheader={`Evolución real de EC2 y RDS — ${mesesOrdenados.length} períodos disponibles`}
                    />
                    <Divider />
                    <CardContent>
                      <ReactECharts option={lineChartOption} style={{ height: "400px" }} />
                      {trendData.length > 0 && (
                        <Box
                          sx={{
                            mt: 2,
                            display: "flex",
                            gap: 2,
                            flexWrap: "wrap",
                            justifyContent: "flex-end",
                          }}
                        >
                          {trendData.map((d) => (
                            <Chip
                              key={d.mes}
                              label={`${d.label}: ${currency.format(d.total)}`}
                              size="small"
                              variant={d.mes === ultimoMes ? "filled" : "outlined"}
                              color={d.mes === ultimoMes ? "primary" : "default"}
                              sx={{ fontWeight: 600 }}
                            />
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* ── Tab 3: Tabla Pivote (Servicio × Mes) ─────────────────── */}
                {tabValue === 3 && (
                  <Card elevation={2} sx={{ borderRadius: 3 }}>
                    <CardHeader
                      avatar={<TableChartIcon sx={{ color: "#137af0" }} />}
                      title={
                        <Typography variant="h6" fontWeight={700}>
                          Desglose por Servicio y Período
                        </Typography>
                      }
                      subheader={`${pivotData.length} servicios · ${mesesOrdenados.length} períodos`}
                    />
                    <Divider />
                    <CardContent sx={{ p: 0, overflowX: "auto" }}>
                      {rows.length === 0 ? (
                        <Alert severity="info" sx={{ m: 2, borderRadius: 2 }}>
                          <Typography variant="body1" fontWeight={600}>
                            No se encontraron registros de costos para este cliente
                          </Typography>
                        </Alert>
                      ) : (
                        <Box
                          component="table"
                          sx={{
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: "0.875rem",
                            "& thead tr": {
                              background: "linear-gradient(90deg, #edf5f1 100%, #137af0 0%)",
                              color: "white",
                            },
                            "& thead th": {
                              py: 1.5,
                              px: 2,
                              textAlign: "left",
                              fontWeight: 700,
                              fontSize: "0.78rem",
                              letterSpacing: 0.8,
                              textTransform: "uppercase",
                              whiteSpace: "nowrap",
                              borderRight: "1px solid rgba(255,255,255,0.1)",
                            },
                            "& tbody tr": {
                              transition: "background 0.2s",
                              "&:nth-of-type(even)": { backgroundColor: "#f8fafc" },
                              "&:hover": { backgroundColor: "#e8f0fe" },
                              borderBottom: "1px solid #e9ecef",
                            },
                            "& tbody td": {
                              py: 1.2,
                              px: 2,
                              verticalAlign: "middle",
                              whiteSpace: "nowrap",
                              borderRight: "1px solid #f0f0f0",
                            },
                            "& tfoot tr": {
                              background: "linear-gradient(90deg, #edf0f3 100%, #0d5fbf 0%)",
                              color: "white",
                            },
                            "& tfoot td": {
                              py: 1.5,
                              px: 2,
                              fontWeight: 800,
                              fontSize: "0.82rem",
                              whiteSpace: "nowrap",
                              borderRight: "1px solid rgba(255,255,255,0.15)",
                            },
                          }}
                        >
                          <thead>
                            <tr>
                              <th style={{ minWidth: 140 }}>Artefacto</th>
                              <th style={{ minWidth: 220 }}>Servicio / Tipo</th>
                              {mesesOrdenados.map((mes, i) => (
                                <th
                                  key={mes}
                                  style={{
                                    minWidth: 160,
                                    textAlign: "right",
                                    borderLeft: "3px solid",
                                  }}
                                >
                                  {formatAnioMes(mes)}
                                </th>
                              ))}
                              <th
                                style={{
                                  minWidth: 160,
                                  textAlign: "right",
                                  borderLeft: "3px solid #0c0c0c",
                                }}
                              >
                                Total Acumulado
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {pivotData.map((row, idx) => {
                              const isEC2 = row.artefacto?.toUpperCase() === "EC2";
                              return (
                                <tr key={row.tipo}>
                                  <td>
                                    <Chip
                                      icon={
                                        isEC2 ? (
                                          <CloudIcon sx={{ fontSize: 14 }} />
                                        ) : (
                                          <StorageIcon sx={{ fontSize: 14 }} />
                                        )
                                      }
                                      label={row.artefacto || "—"}
                                      size="small"
                                      color={isEC2 ? "info" : "success"}
                                      sx={{ fontWeight: 700, fontSize: "0.72rem" }}
                                    />
                                  </td>
                                  <td>
                                    <Typography
                                      variant="body2"
                                      fontWeight={600}
                                      sx={{ color: "#1a1a2e" }}
                                    >
                                      {row.tipo}
                                    </Typography>
                                  </td>
                                  {mesesOrdenados.map((mes, i) => {
                                    const val = row[mes] || 0;
                                    const maxVal = Math.max(...pivotData.map((d) => d[mes] || 0));
                                    const pct = maxVal > 0 ? (val / maxVal) * 100 : 0;
                                    return (
                                      <td key={mes} style={{ textAlign: "right" }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "flex-end",
                                            gap: 0.3,
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            fontWeight={700}
                                            sx={{ color: val > 0 ? "#1a1a2e" : "#bbb" }}
                                          >
                                            {val > 0 ? currencyDetailed.format(val) : "—"}
                                          </Typography>
                                          {val > 0 && (
                                            <Box
                                              sx={{
                                                width: "100%",
                                                maxWidth: 100,
                                                height: 4,
                                                borderRadius: 2,
                                                backgroundColor: "#e9ecef",
                                                overflow: "hidden",
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  width: `${pct}%`,
                                                  height: "100%",
                                                  borderRadius: 2,
                                                  backgroundColor:
                                                    MONTH_COLORS[i % MONTH_COLORS.length],
                                                  transition: "width 0.5s ease",
                                                }}
                                              />
                                            </Box>
                                          )}
                                        </Box>
                                      </td>
                                    );
                                  })}
                                  <td style={{ textAlign: "right" }}>
                                    <Typography
                                      variant="body2"
                                      fontWeight={800}
                                      sx={{ color: "#0c0c0c" }}
                                    >
                                      {currencyDetailed.format(row.total)}
                                    </Typography>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td colSpan={2}>TOTAL DEL PERÍODO</td>
                              {mesesOrdenados.map((mes) => {
                                const total = pivotData.reduce((acc, d) => acc + (d[mes] || 0), 0);
                                return (
                                  <td key={mes} style={{ textAlign: "right" }}>
                                    {currencyDetailed.format(total)}
                                  </td>
                                );
                              })}
                              <td style={{ textAlign: "right" }}>
                                {currencyDetailed.format(
                                  pivotData.reduce((acc, d) => acc + d.total, 0)
                                )}
                              </td>
                            </tr>
                          </tfoot>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
};

CostosCliente.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      database_name: PropTypes.string,
      total: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  handleCloseModal: PropTypes.func,
  onClose: PropTypes.func,
};

export default CostosCliente;
